import { FunctionComponent, useEffect, useRef } from 'react';
import { Box, Modal, withStyles, CircleButton } from 'vopay';
import { Title } from 'UI';
import styles from './ChangePasswordDialogue.module.scss';
import { useTranslation, Trans } from 'react-i18next';
import ChangePasswordForm from './ChangePasswordForm';
import usePostPortalClientLogin from '@/hooks/useApi/usePostPortalClientLogin';
import useChangeAccountUserPassword from '@/hooks/useApi/useChangeAccountUserPassword';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';

export interface ChangePasswordDialogueProps {
  isVisible: boolean;
  onCloseModal: () => void;
  email: string;
}

type ChangePasswordData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const classes = withStyles(styles);

const ChangePasswordDialogue: FunctionComponent<ChangePasswordDialogueProps> = ({ isVisible, onCloseModal, email }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList.ChangePassword' });
  const alertDialog = useAlertDialog();
  const [, , , postCurrentPassword] = usePostPortalClientLogin({});
  const { changePassword } = useChangeAccountUserPassword();
  const [, setPending] = useDataFetchIndicator();

  useEffect(() => {
    if (isVisible && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const controller = new AbortController();
    const { signal } = controller;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        onCloseModal();
        controller.abort();
      }
    };
    window.addEventListener('keydown', handleKeyDown, { signal });
    return () => {
      controller.abort();
    };
  }, [isVisible, onCloseModal]);

  const submit = async (values: ChangePasswordData) => {
    try {
      setPending(true);
      const loginRes = await postCurrentPassword({ Username: email, Password: values.currentPassword });
      if (!loginRes.Success) throw new Error(loginRes.ErrorMessage);
      const changePasswordRes = await changePassword({
        EmailAddress: email,
        NewPassword: values.newPassword,
        SendEmail: true,
      });
      if (!changePasswordRes.Success) throw new Error(changePasswordRes.ErrorMessage);
      setPending(false);
      const closeType = await alertDialog.success(t('Alert.success'), t('Alert.newPassword'), {
        abortLabel: tGlobal('Buttons.close'),
      });
      if (closeType === 'abort') {
        onCloseModal();
      }
    } catch (err) {
      setPending(false);
      console.error(err);
      const closeType = await alertDialog.error(
        <Trans i18nKey="Users.UsersList.ChangePassword.Alert.error" ns="translations" components={{ br: <br /> }} />,
        t('Alert.currentPassword'),
        {
          abortLabel: tGlobal('Buttons.close'),
          confirmLabel: t('Button.goBack'),
        },
      );
      if (closeType === 'abort') {
        onCloseModal();
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal isVisible={isVisible} enableBgClick={false}>
      <div ref={containerRef} className={classes('container', 'scrollbar')}>
        <div className={classes('header')}>
          <Box jc="sb" ai="center">
            <Title level="2">{t('title')}</Title>
            <CircleButton icon="close" size="large" colorScheme="neutral" onClick={onCloseModal} />
          </Box>
          <p className={classes('description')}>{email}</p>
        </div>
        <ChangePasswordForm submit={submit} onClose={onCloseModal} />
      </div>
    </Modal>
  );
};

export default ChangePasswordDialogue;
